import {SettingParam} from "@/types/node";
import {useEffect, useState, useCallback, memo, useMemo} from "react";
import {Button} from "../ui/Button";
import {Plus, X} from "lucide-react";
import {SettingComponentMap} from "@/components/settings/index";

export interface SettingItemProps {
  key: string;
  value: string;
}

const SettingItem = memo(
  ({
    param,
    item,
    itemIndex,
    onItemDelete,
    onChange,
  }: {
    param: SettingParam;
    item: SettingItemProps[];
    itemIndex: number;
    onItemDelete: (index: number) => void;
    onChange: (item: SettingItemProps[], index: number) => void;
  }) => {
    const itemSettings: SettingParam[] = param.settings;

    const handleSettingChange = useCallback(
      (param: SettingParam, value: any, index: number) => {
        const updated = item.map(setting =>
          setting.key === param.key ? {...setting, value} : setting,
        );
        onChange(updated, index);
      },
      [item, onChange],
    );

    return (
      <div className="flex flex-row justify-between items-start">
        <div className="flex flex-row items-start gap-2 w-full mr-2">
          {itemSettings.map((setting: SettingParam, index: number) => {
            const SettingComponent =
              SettingComponentMap[
                setting.type as keyof typeof SettingComponentMap
              ];

            const itemValue = Array.isArray(item)
              ? item.find(itemObj => itemObj.key === setting.key)?.value
              : "";

            const param = {
              ...setting,
              value: itemValue || "",
              label: itemIndex === 0 ? setting.label : "",
            };

            return SettingComponent ? (
              <SettingComponent
                key={setting.key}
                param={param}
                index={index}
                handleChange={(param, value) =>
                  handleSettingChange(param, value, itemIndex)
                }
                placeholder={param.placeholder}
                selectOptions={param.selectOptions}
                className={param.className}
              />
            ) : null;
          })}
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          className="[&_svg]:size-5 ml-auto mt-1"
          onClick={e => {
            e.stopPropagation();
            onItemDelete(itemIndex);
          }}
        >
          <X className="text-secondary" />
        </Button>
      </div>
    );
  },
);

SettingItem.displayName = "SettingItem";

export const SettingMultiple = ({
  param,
  handleChange,
  selectedNodeId,
  error,
  disabled = false,
}: {
  param: SettingParam & {value: SettingItemProps[]};
  handleChange: (param: SettingParam, value: SettingItemProps[]) => void;
  selectedNodeId: string;
  disabled: boolean;
  error?: string;
}) => {
  const [values, setValues] = useState<any>(param.value || []);

  // Only reset values when node changes, not on every render
  // Intentionally not including param.value to avoid resetting on every keystroke
  useEffect(() => {
    setValues(param.value || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId]);

  const handleVariableDelete = useCallback(
    (index: number) => {
      setValues(prev => {
        const newValues = [...prev];
        newValues.splice(index, 1);
        handleChange(param, newValues);
        return newValues;
      });
    },
    [param, handleChange],
  );

  const handleItemChange = useCallback(
    (item: SettingItemProps[], index: number) => {
      setValues(prev => {
        const newValues = [...prev];
        newValues[index] = item;
        handleChange(param, newValues);
        return newValues;
      });
    },
    [param, handleChange],
  );

  const emptyItem: SettingItemProps[] = useMemo(
    () =>
      param.settings.map(setting => ({
        key: setting.key,
        value: "",
      })),
    [param.settings],
  );

  return (
    <div>
      <Button
        variant="outline"
        className="w-full px-3 gap-1.5"
        asDiv
        disabled={disabled}
        onClick={() => {
          setValues(prev => {
            const newValues = [...prev, emptyItem];
            return newValues;
          });
        }}
      >
        <Plus className="text-primary-accent w-4 h-4" />
        Add Item
      </Button>

      <div className="flex flex-col mt-4">
        {values.map((item, index) => (
          <SettingItem
            key={`${index}-${item[0]?.key || ""}`}
            param={param}
            item={item}
            itemIndex={index}
            onItemDelete={handleVariableDelete}
            onChange={handleItemChange}
          />
        ))}
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    </div>
  );
};
